// packages/constants/src/rankings.js
//
// Custom Rankings (docs/custom-rankings-architecture-audit.md): a user's own
// Top 5 / 10 / 15 / 20 of movies, TV series or both, under a title they choose.
// A ranking starts as a Top 5 or Top 10 (RANKING_START_SIZES); the editor can
// expand it to a Top 15 or Top 20 without losing the titles already picked.
// Shared by web and mobile; the backend mirrors these rules in
// apps/backend/src/services/rankingService.js and a backend test fails if the
// two drift.
//
// A title is identified by (kind, tmdbId): movie 603 and TV 603 are different.
//
// Drafts and publishing: a new ranking is a private DRAFT on the server and
// may be incomplete (6 of a Top 10, no title yet). Only the owner sees it.
// Publishing is explicit and needs a valid title and exactly `size` distinct
// titles, so a published Top 20 always has 20. A published ranking keeps a
// separate published version: the owner's later saves stay private until
// they update the published ranking (or discard them).

import { DEFAULT_SHARE_FOOTER, SHARE_FOOTER_CHOICES, SHARE_FOOTER_MESSAGES, shareFooterQuery } from './shareFooter';

export const RANKING_SIZES = [5, 10, 15, 20];
/** The choices offered when starting a ranking; 15 and 20 are reached by expanding. */
export const RANKING_START_SIZES = [5, 10];
export const MAX_RANKING_SIZE = 20;
/** Share images of longer rankings (Top 15 / 20) default to a preview of this many. */
export const RANKING_PREVIEW_COUNT = 5;
export const RANKING_KINDS = ['movie', 'tv'];
export const RANKING_VISIBILITIES = ['private', 'public'];
/** 'draft' (only the owner sees it) | 'published' (its published version is public). */
export const RANKING_STATUSES = ['draft', 'published'];
export const MAX_RANKING_TITLE_LENGTH = 80;
export const MAX_RANKING_DESCRIPTION_LENGTH = 300;

/** Local draft key for R2's guest editor (one draft at a time). */
export const RANKING_DRAFT_STORAGE_KEY = 'aluvo_ranking_draft';

/** "movie:603" — the identity of a title inside a ranking. */
export const rankingItemKey = (item) => `${item.kind}:${Number(item.tmdbId)}`;

// Control characters, zero-width characters and bidi overrides never belong
// in a title: they can hide or reorder text in previews and share images.
const INVISIBLE = /[\u0000-\u001F\u007F-\u009F​-‏‪-‮⁠-⁤⁦-⁩﻿]/g;

/** The stored form of a title: invisibles removed, whitespace collapsed, trimmed. */
export function normalizeRankingTitle(raw) {
    if (typeof raw !== 'string') return '';
    return raw.replace(INVISIBLE, ' ').replace(/\s+/g, ' ').trim();
}

/** Length in characters as people count them (code points, not UTF-16 units). */
export const rankingTitleLength = (title) => [...String(title)].length;

/** Descriptions keep line breaks (at most one blank line in a row); each line is cleaned like a title. */
export function normalizeRankingDescription(raw) {
    if (typeof raw !== 'string') return '';
    return raw.split(/\r\n|\r|\n/).map(normalizeRankingTitle).join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

export function isValidRankingTitle(raw) {
    const title = normalizeRankingTitle(raw);
    return title.length > 0 && rankingTitleLength(title) <= MAX_RANKING_TITLE_LENGTH;
}

/** 'movie' | 'tv' | 'mixed', or null for no items. */
export function rankingComposition(items) {
    const kinds = new Set((items || []).map((item) => item.kind));
    if (kinds.size === 0) return null;
    if (kinds.size > 1) return 'mixed';
    return kinds.has('tv') ? 'tv' : 'movie';
}

// ── Editor drafts (web localStorage / mobile AsyncStorage) ───────────────────
//
// A draft is the editor's local, unsaved work. It is not permanent storage:
// it lives on one device and expires after RANKING_DRAFT_MAX_AGE_MS.
//   draftId      UUID made by the client; sent as clientDraftId so a save is
//                idempotent (a repeated save returns the same ranking)
//   sourceSlug   the public ranking a "Make Your Own" started from
//   pendingSave  set when a guest leaves to sign in / sign up to save it:
//                true (save as a draft) or 'publish' (save, then publish)
export const RANKING_DRAFT_VERSION = 1;
export const RANKING_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * A random UUID v4 for a new draft, sent as clientDraftId so saving is
 * idempotent. Uses crypto.randomUUID where the platform has it (browsers);
 * Hermes may not, so falls back to Math.random — uniqueness only matters per user.
 */
export function newRankingDraftId() {
    const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    const bytes = Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // eslint-disable-line no-bitwise
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // eslint-disable-line no-bitwise
    const hex = bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function emptyRankingDraft({ draftId, title = '', size = 5, sourceSlug = null } = {}) {
    return {
        v: RANKING_DRAFT_VERSION,
        draftId,
        title,
        size: RANKING_SIZES.includes(size) ? size : 5,
        items: [],
        description: '',
        // Whether the public page shows 1, 2, 3… on the posters. The order is
        // saved either way; this is presentation only.
        showPositions: true,
        sourceSlug,
        pendingSave: false,
    };
}

/** The fields a ranking needs from a search result or a saved item. */
export function toRankingItem(media) {
    return {
        kind: media.kind === 'tv' ? 'tv' : 'movie',
        tmdbId: Number(media.tmdbId ?? media.id),
        title: media.title || 'Untitled',
        year: media.year ?? null,
        posterUrl: media.posterUrl ?? null,
    };
}

const hasItem = (items, key, except = -1) => items.some((item, i) => i !== except && rankingItemKey(item) === key);

/** Add at the end. status: 'added' | 'duplicate' | 'full' */
export function addRankingItem(draft, media) {
    const item = toRankingItem(media);
    if (hasItem(draft.items, rankingItemKey(item))) return { draft, status: 'duplicate' };
    if (draft.items.length >= draft.size) return { draft, status: 'full' };
    return { draft: { ...draft, items: [...draft.items, item] }, status: 'added' };
}

/** Put a new title in slot `index` (keeps the rank). status: 'replaced' | 'duplicate' | 'invalid' */
export function replaceRankingItem(draft, index, media) {
    if (index < 0 || index >= draft.items.length) return { draft, status: 'invalid' };
    const item = toRankingItem(media);
    if (hasItem(draft.items, rankingItemKey(item), index)) return { draft, status: 'duplicate' };
    const items = [...draft.items];
    items[index] = item;
    return { draft: { ...draft, items }, status: 'replaced' };
}

export function removeRankingItem(draft, index) {
    return { ...draft, items: draft.items.filter((_, i) => i !== index) };
}

/** Move the title at `from` to `to` (drag-and-drop or the up / down buttons). */
export function moveRankingItem(draft, from, to) {
    const n = draft.items.length;
    if (from === to || from < 0 || to < 0 || from >= n || to >= n) return draft;
    const items = [...draft.items];
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    return { ...draft, items };
}

/** Titles a switch to `size` would remove (ranks size+1 and below). */
export const rankingOverflow = (draft, size) => draft.items.slice(size);

/** The larger sizes a ranking of `size` can expand to (Top 5 → 10, 15, 20). */
export const rankingExpansionSizes = (size) => RANKING_SIZES.filter((s) => s > size);

/** Top 15 / Top 20: long enough that share images default to a preview. */
export const isLongRanking = (size) => Number(size) > 10;

/** What the editor says when a pick doesn't fit. */
export function rankingFullNotice(size) {
    const next = rankingExpansionSizes(size)[0];
    return next
        ? `Your Top ${size} is full. Remove or replace one, or expand it to a Top ${next}.`
        : `Your Top ${size} is full. Remove or replace one to make room.`;
}

/**
 * Change the size. Growing keeps every title and its rank. Shrinking drops
 * the titles below the new size, so callers must confirm first when
 * rankingOverflow() is not empty.
 */
export function setRankingSize(draft, size) {
    if (!RANKING_SIZES.includes(size) || size === draft.size) return draft;
    return { ...draft, size, items: draft.items.slice(0, size) };
}

/** "6 of 10 selected" */
export const rankingProgressLabel = (count, size) => `${count} of ${size} selected`;

/** What still blocks saving a DRAFT (drafts may be untitled and incomplete); [] when it can be saved. */
export function rankingDraftProblems(draft) {
    const problems = [];
    if (rankingTitleLength(normalizeRankingTitle(draft.title)) > MAX_RANKING_TITLE_LENGTH) problems.push(`Keep the title to ${MAX_RANKING_TITLE_LENGTH} characters.`);
    if (rankingTitleLength(normalizeRankingDescription(draft.description || '')) > MAX_RANKING_DESCRIPTION_LENGTH) {
        problems.push(`Keep the description to ${MAX_RANKING_DESCRIPTION_LENGTH} characters.`);
    }
    return problems;
}

/**
 * What still blocks PUBLISHING, in order; [] when the ranking can be
 * published. The server checks the same rules and says the same thing.
 */
export function rankingProblems(draft) {
    const problems = [];
    if (!isValidRankingTitle(draft.title)) {
        problems.push(normalizeRankingTitle(draft.title) ? `Keep the title to ${MAX_RANKING_TITLE_LENGTH} characters.` : 'Give your ranking a title before publishing it.');
    }
    const missing = draft.size - draft.items.length;
    if (missing > 0) problems.push(`Add ${missing} more ${missing === 1 ? 'title' : 'titles'} to publish your Top ${draft.size}.`);
    if (rankingTitleLength(normalizeRankingDescription(draft.description || '')) > MAX_RANKING_DESCRIPTION_LENGTH) {
        problems.push(`Keep the description to ${MAX_RANKING_DESCRIPTION_LENGTH} characters.`);
    }
    return problems;
}

/** Ready to publish: a valid title and every slot filled. */
export const isRankingComplete = (draft) => rankingProblems(draft).length === 0;

/**
 * Request body for POST /rankings (a new draft) and, without the create-only
 * fields, PUT /rankings/me/:id (save the working version) and POST
 * /rankings/me/:id/publish (save and publish). Never sends `visibility`:
 * that is the older clients' publish / unpublish switch.
 */
export function rankingPayload(draft, { forCreate = true } = {}) {
    return {
        title: normalizeRankingTitle(draft.title),
        description: normalizeRankingDescription(draft.description || '') || null,
        size: draft.size,
        items: draft.items.map((item) => ({ kind: item.kind, tmdbId: item.tmdbId })),
        showPositions: draft.showPositions !== false,
        ...(forCreate ? {
            clientDraftId: draft.draftId || undefined,
            sourceSlug: draft.sourceSlug || undefined,
        } : {}),
    };
}

/** Editor state from a saved ranking's working version (GET /rankings/me/:id). */
export function rankingDraftFromSaved(saved) {
    return {
        v: RANKING_DRAFT_VERSION,
        draftId: null,
        title: saved.title || '',
        size: saved.size,
        items: (saved.items || []).map(toRankingItem),
        description: saved.description || '',
        showPositions: saved.showPositions !== false,
        sourceSlug: null,
        pendingSave: false,
    };
}

/** True when the editor holds exactly the saved working version. */
export function rankingMatchesSaved(draft, saved) {
    if (!saved) return false;
    return normalizeRankingTitle(draft.title) === (saved.title || '')
        && (normalizeRankingDescription(draft.description || '') || null) === (saved.description || null)
        && draft.size === saved.size
        && (draft.showPositions !== false) === (saved.showPositions !== false)
        && draft.items.length === saved.items.length
        && draft.items.every((item, i) => rankingItemKey(item) === rankingItemKey(saved.items[i]));
}

/** Where a saved ranking stands, for the editor and the owner's lists. */
export function rankingStatusOf(saved) {
    if (!saved) return { status: 'new', label: 'Not saved yet', changes: false };
    if (saved.status === 'published') {
        return { status: 'published', label: 'Published', changes: Boolean(saved.hasUnpublishedChanges) };
    }
    return { status: 'draft', label: 'Draft', changes: false };
}

/**
 * A draft read back from storage, normalized; null when missing, corrupt or
 * older than RANKING_DRAFT_MAX_AGE_MS (local drafts are not permanent).
 */
export function normalizeStoredRankingDraft(parsed, now = Date.now()) {
    if (!parsed || typeof parsed !== 'object' || parsed.v !== RANKING_DRAFT_VERSION) return null;
    if (!RANKING_SIZES.includes(parsed.size) || !Array.isArray(parsed.items)) return null;
    const updated = Date.parse(parsed.updatedAt || '');
    if (!Number.isFinite(updated) || now - updated > RANKING_DRAFT_MAX_AGE_MS) return null;
    const seen = new Set();
    const items = parsed.items
        .filter((item) => item && RANKING_KINDS.includes(item.kind) && Number.isInteger(Number(item.tmdbId)) && Number(item.tmdbId) > 0)
        .map(toRankingItem)
        .filter((item) => {
            const key = rankingItemKey(item);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        .slice(0, parsed.size);
    return {
        ...emptyRankingDraft({ draftId: typeof parsed.draftId === 'string' ? parsed.draftId : undefined }),
        title: typeof parsed.title === 'string' ? parsed.title.slice(0, 200) : '',
        size: parsed.size,
        items,
        description: typeof parsed.description === 'string' ? parsed.description.slice(0, 1000) : '',
        showPositions: parsed.showPositions !== false,
        sourceSlug: typeof parsed.sourceSlug === 'string' ? parsed.sourceSlug : null,
        pendingSave: parsed.pendingSave === 'publish' ? 'publish' : parsed.pendingSave === true,
        updatedAt: parsed.updatedAt,
    };
}

// ── Share images ─────────────────────────────────────────────────────────────
//
// The server renders every share image (GET /share/rankings/:slug/card.png for
// a saved public ranking, /rankings/card.png?items=… for anything else); the
// editor and public page only choose options and show the PNG the server
// returns, so the preview and the exported file are the same request.
//
// Options are a per-viewer presentation choice for the image only. They never
// change the ranking: `numbers` starts from the ranking's own showPositions
// and can be turned off or on for the image alone.
//   format   'square' | 'story'
//   layout   'preview' (the first RANKING_PREVIEW_COUNT, with "+N more on
//            Aluvo") | 'full' — only Top 15 / 20 have a choice; a full Top 15
//            or 20 is only legible in the tall Story image
//   style    'grid' (Poster Grid: big posters, the original image) | 'list'
//            (Compact List: a small poster beside every title, the whole
//            ranking in one or two columns). Older saved options have no
//            style and stay Poster Grid. A Compact List always draws every
//            saved title, so `layout` only applies to the grid
//   show     { title, owner, numbers, captions }; numbers: null means
//            "same as the public page". A Compact List always shows the titles
//            (they are the list), but the stored `captions` choice is kept for
//            the grid
//   footer   the Footer message choice (./shareFooter): 'default' is
//            "aluvo.app · Make your own Top N." with the saved count. Options
//            saved before the selector carry `show.slogan` instead: true (an
//            explicit pick — the switch was off by default) becomes 'prove';
//            false was the old default and becomes 'default'

export const RANKING_CARD_FORMATS = ['square', 'story'];
export const RANKING_CARD_LAYOUTS = ['preview', 'full'];
export const RANKING_CARD_STYLES = ['grid', 'list'];
export const RANKING_SLOGAN = SHARE_FOOTER_MESSAGES.prove;

export const RANKING_CARD_STYLE_OPTIONS = [
    { key: 'grid', label: 'Poster Grid', sub: 'Big posters with their titles underneath' },
    { key: 'list', label: 'Compact List', sub: 'A small poster beside every title, the whole list' },
];

/** Compact List is suggested (never picked for the user) for a Top 15 / 20. */
export const isCompactListSuggested = (size) => isLongRanking(size);

/**
 * Formats where a Compact List of `size` stays readable on a phone. Two
 * columns of 8–10 rows only fit with legible text in the tall Story image;
 * a Square would have to shrink the titles too far. The renderer mirrors this
 * (apps/backend/src/services/listShareCardService.js, rankingListFormatsFor).
 */
export function rankingListFormatsFor(size) {
    return isLongRanking(size) ? ['story'] : RANKING_CARD_FORMATS;
}

/** Which formats can hold a style / layout for a ranking of `size`. */
export function rankingCardFormatsFor(size, layout, style = 'grid') {
    if (style === 'list') return rankingListFormatsFor(size);
    return isLongRanking(size) && layout === 'full' ? ['story'] : RANKING_CARD_FORMATS;
}

/** Why a format is missing for a style, or null when every format is offered. */
export function rankingCardFormatNote(size, layout, style = 'grid') {
    if (rankingCardFormatsFor(size, layout, style).includes('square')) return null;
    return style === 'list'
        ? `A Compact List of ${size} titles needs the tall Story size: in a Square the titles would be too small to read on a phone.`
        : `A full Top ${size} is only readable in the tall Story size.`;
}

export const RANKING_CARD_PARTS = [
    { key: 'title', label: 'Headline', sub: 'The ranking’s title, large at the top' },
    { key: 'owner', label: 'Your @username', sub: 'Your Aluvo username under the title' },
    { key: 'numbers', label: 'Position numbers', sub: '1, 2, 3… on the posters (the order is kept either way)' },
    { key: 'captions', label: 'Movie & series titles', sub: 'Title and year under each poster' },
];

export const DEFAULT_RANKING_CARD_OPTIONS = {
    format: 'square',
    layout: 'preview',
    style: 'grid',
    show: { title: true, owner: true, numbers: null, captions: true },
    footer: DEFAULT_SHARE_FOOTER,
};

const bool = (value, fallback) => (typeof value === 'boolean' ? value : fallback);

/** Options read back from storage (or anything else), made valid. Never throws. */
export function normalizeRankingCardOptions(raw) {
    const d = DEFAULT_RANKING_CARD_OPTIONS;
    const show = raw && typeof raw.show === 'object' && raw.show ? raw.show : {};
    return {
        format: RANKING_CARD_FORMATS.includes(raw?.format) ? raw.format : d.format,
        layout: RANKING_CARD_LAYOUTS.includes(raw?.layout) ? raw.layout : d.layout,
        style: RANKING_CARD_STYLES.includes(raw?.style) ? raw.style : d.style,
        show: {
            title: bool(show.title, d.show.title),
            owner: bool(show.owner, d.show.owner),
            numbers: typeof show.numbers === 'boolean' ? show.numbers : null,
            captions: bool(show.captions, d.show.captions),
        },
        footer: SHARE_FOOTER_CHOICES.includes(raw?.footer) ? raw.footer : show.slogan === true ? 'prove' : d.footer,
    };
}

/**
 * What the image will actually be for a ranking: the layout only applies to a
 * Poster Grid of a Top 15 / 20 (a Compact List is always the full list), the
 * format follows the style and layout, a Compact List always shows titles,
 * and numbers fall back to the ranking's public setting. The stored options
 * are not changed, so switching back restores the grid's own choices.
 */
export function resolveRankingCardOptions(options, { size, showPositions = true }) {
    const o = normalizeRankingCardOptions(options);
    const { style } = o;
    const layout = style === 'grid' && isLongRanking(size) ? o.layout : 'full';
    const formats = rankingCardFormatsFor(size, layout, style);
    return {
        format: formats.includes(o.format) ? o.format : formats[0],
        layout,
        style,
        footer: o.footer,
        show: {
            ...o.show,
            numbers: o.show.numbers ?? showPositions !== false,
            captions: style === 'list' ? true : o.show.captions,
        },
    };
}

/**
 * Query string for the card endpoints. Everything is explicit (numbers and
 * layout included), so the URL alone decides the picture — what the preview
 * shows is what gets exported, and no cached image can belong to other options.
 * `personal`: false for the id-only preview card, which never carries a title
 * or username.
 */
export function rankingCardQuery(resolved, { personal = true } = {}) {
    const { format, layout, show } = resolved;
    const list = resolved.style === 'list';
    // No `style` means Poster Grid, so grid URLs are exactly the ones shared
    // before Compact List existed.
    const params = [...(list ? ['style=list'] : []), `format=${format}`, `layout=${layout}`, `numbers=${show.numbers ? 1 : 0}`];
    if (!show.captions && !list) params.push('captions=0');
    params.push(shareFooterQuery(resolved.footer));
    if (personal) {
        if (!show.title) params.push('title=0');
        if (!show.owner) params.push('owner=0');
    }
    return params.join('&');
}

/** "Showing the first 5 of 20" — never "#1–#5" when numbers are hidden. */
export function rankingCardLayoutLabel(resolved, size) {
    if (!isLongRanking(size)) return null;
    if (resolved.layout === 'full') return `Full list · all ${size} titles`;
    return `Preview · the first ${RANKING_PREVIEW_COUNT} of ${size}, with a note that the full list is on Aluvo`;
}
